/**
 * Reply Templates — /settings/reply-templates  (Iter 113)
 *
 * Admin CRUD for canned chat/ticket replies. Every staff member sees
 * these templates as chips in the CRM chat composer + the CRM Tickets
 * modal so they can reply in one tap.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import {
  Plus, X, PencilSimple, Trash, FloppyDisk, ChatCircle,
  MagnifyingGlass, Tag, ChartBar,
} from "@phosphor-icons/react";
import { TEMPLATE_TOKENS } from "../../lib/templateTokens";

export default function ReplyTemplates() {
  const { can } = useAuth();
  const canEdit = can(["superadmin", "admin", "manager"]);
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | {} (new) | template

  const load = async () => {
    const [tpls, tg] = await Promise.all([
      api.get(`/reply-templates${tag ? `?tag=${encodeURIComponent(tag)}` : ""}`),
      api.get("/reply-templates/tags"),
    ]);
    setItems(tpls.data);
    setTags(tg.data);
  };
  useEffect(() => { load(); }, [tag]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return items;
    return items.filter(t =>
      t.title.toLowerCase().includes(qq) ||
      t.body.toLowerCase().includes(qq) ||
      (t.tags || []).some(tg => tg.includes(qq))
    );
  }, [items, q]);

  const remove = async (id) => {
    if (!window.confirm("Remove this template? Staff will no longer see it in the picker.")) return;
    try { await api.delete(`/reply-templates/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Reply Templates" subtitle="Settings · CRM"
      actions={canEdit && (
        <button data-testid="rt-new"
                onClick={() => setEditing({ title: "", body: "", tags: [] })}
                className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
          <Plus size={14} weight="bold"/>New template
        </button>
      )}
    >
      <div className="bg-white border border-[#E5E2D9] rounded-md p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">Search</label>
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-2.5 text-[#8A8780]"/>
            <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="Title, body or tag"
                    data-testid="rt-search"
                    className="w-full h-9 pl-8 pr-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">Tag filter</label>
          <div className="flex gap-1 flex-wrap max-w-[540px]">
            <button data-testid="rt-tag-all"
                    onClick={() => setTag("")}
                    className={`h-8 px-2 rounded-full text-[11px] font-medium border ${
                      tag === "" ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                  : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:border-[#B7B3A6]"
                    }`}>All</button>
            {tags.map(t => (
              <button key={t.tag}
                      data-testid={`rt-tag-${t.tag}`}
                      onClick={() => setTag(t.tag)}
                      className={`h-8 px-2.5 rounded-full text-[11px] font-medium border inline-flex items-center gap-1 ${
                        tag === t.tag ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                       : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:border-[#B7B3A6]"
                      }`}>
                <Tag size={10} weight={tag === t.tag ? "fill" : "regular"}/>
                {t.tag}
                <span className="opacity-60">({t.count})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="rt-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Body</th>
              <th className="px-4 py-3">Tags</th>
              <th className="px-4 py-3">Uses</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-14 text-center text-[#8A8780]">
                <ChatCircle size={26} className="inline-block mb-3 opacity-40"/><br/>
                No templates yet. {canEdit && "Click \u201cNew template\u201d to create your first canned reply."}
              </td></tr>
            )}
            {filtered.map((t, i) => (
              <tr key={t.id} data-testid={`rt-row-${t.id}`}
                  className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                <td className="px-4 py-3 font-medium text-[#1A1A1A] whitespace-nowrap">{t.title}</td>
                <td className="px-4 py-3 text-[#5C5A55] max-w-md">
                  <div className="line-clamp-2 whitespace-pre-wrap text-[13px]">{t.body}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(t.tags || []).map(tg => (
                      <span key={tg} className="text-[9px] uppercase tracking-[0.1em] font-semibold px-1.5 py-0.5 rounded bg-[#F0F7EE] text-[#1A3317] border border-[#B5D5AC]">
                        {tg}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap"
                    data-testid={`rt-usage-${t.id}`}>
                  <UsageChip count={t.use_count || 0} last={t.last_used_at}/>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {canEdit && (
                    <>
                      <button onClick={() => setEditing(t)}
                              data-testid={`rt-edit-${t.id}`}
                              title="Edit template"
                              className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#F0EFEA] text-[#5C5A55] mr-1">
                        <PencilSimple size={14} weight="bold"/>
                      </button>
                      <button onClick={() => remove(t.id)}
                              data-testid={`rt-del-${t.id}`}
                              title="Remove template"
                              className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#F4C7B9]/50 text-[#7A2E18]">
                        <Trash size={14} weight="bold"/>
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditModal
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </AppShell>
  );
}

function UsageChip({ count, last }) {
  if (!count) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#F0EFEA] text-[#8A8780] border border-[#E5E2D9]">
        Never used
      </span>
    );
  }
  const heat = count >= 20 ? "bg-[#F4C7B9] text-[#7A2E18] border-[#EFC7B6]"
             : count >= 5  ? "bg-[#F9E4B7] text-[#7A5A11] border-[#F0DDB2]"
             :               "bg-[#F0F7EE] text-[#1A3317] border-[#B5D5AC]";
  const lastLabel = last
    ? new Date(last).toLocaleDateString(undefined, { day: "2-digit", month: "short" })
    : null;
  return (
    <span
      title={last ? `Last used ${new Date(last).toLocaleString()}` : "Used at least once"}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${heat}`}
    >
      <ChartBar size={10} weight="fill"/>
      {count}
      {lastLabel && <span className="opacity-70">· {lastLabel}</span>}
    </span>
  );
}

function EditModal({ template, onClose, onSaved }) {
  const isNew = !template.id;
  const [title, setTitle] = useState(template.title || "");
  const [body, setBody] = useState(template.body || "");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState(template.tags || []);
  const [isAutoAck, setIsAutoAck] = useState(!!template.is_auto_ack);  // Iter 133
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    const t = (tagInput || "").trim().toLowerCase();
    if (!t) return;
    if (tags.includes(t)) { setTagInput(""); return; }
    setTags(prev => [...prev, t]);
    setTagInput("");
  };
  const removeTag = (t) => setTags(prev => prev.filter(x => x !== t));

  const save = async (e) => {
    e.preventDefault();
    if (title.trim().length < 2) return toast.error("Title too short");
    if (body.trim().length < 2) return toast.error("Body too short");
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/reply-templates", { title, body, tags, is_auto_ack: isAutoAck });
        toast.success("Template created");
      } else {
        await api.put(`/reply-templates/${template.id}`, { title, body, tags, is_auto_ack: isAutoAck });
        toast.success("Template updated");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="rt-modal">
      <div className="bg-white rounded-md w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg">{isNew ? "New template" : "Edit template"}</div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"
                  data-testid="rt-modal-close"><X size={16}/></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} required
                    placeholder="e.g. Missed pickup apology"
                    data-testid="rt-title"
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">Body</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} required
                       placeholder="Type the canned reply here. Use plain text — line breaks preserved."
                       data-testid="rt-body"
                       className="w-full min-h-[140px] p-3 border border-[#E5E2D9] rounded-md text-sm"/>
            {/* Iter 114 — token cheat-sheet with tap-to-insert */}
            <div className="mt-2 p-2 bg-[#F7F6F2] border border-[#E5E2D9] rounded-md">
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C5A55] mb-1">
                Personalisation tokens · tap to insert at cursor
              </div>
              <div className="flex flex-wrap gap-1">
                {TEMPLATE_TOKENS.map(t => (
                  <button key={t.key} type="button"
                          data-testid={`rt-token-${t.key}`}
                          onClick={() => setBody(prev => `${prev}{${t.key}}`)}
                          title={`Example: ${t.example}`}
                          className="text-[10px] font-mono px-2 py-0.5 rounded bg-white hover:bg-[#F0F7EE] border border-[#D4E0D1] text-[#1A3317]">
                    {"{" + t.key + "}"}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-[#8A8780] mt-1">
                On send, tokens are replaced with values from the chat.
                Unknown tokens fall back to <em>&quot;there&quot;</em>, <em>&quot;team&quot;</em> etc.
              </div>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">
              Tags <span className="text-[9px] normal-case text-[#8A8780]">press Enter or comma to add</span>
            </label>
            <div className="flex flex-wrap gap-1 mb-2" data-testid="rt-tags">
              {tags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-semibold px-2 py-0.5 rounded bg-[#F0F7EE] text-[#1A3317] border border-[#B5D5AC]">
                  {t}
                  <button type="button" onClick={() => removeTag(t)}
                          data-testid={`rt-tag-remove-${t}`}
                          className="text-[#7A2E18] hover:text-[#5A2013]"><X size={9} weight="bold"/></button>
                </span>
              ))}
            </div>
            <input value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
                    }}
                    onBlur={addTag}
                    placeholder="billing, pickup, bag…"
                    data-testid="rt-tag-input"
                    className="w-full h-9 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
          {/* Iter 133 — grievance auto-ack flag. AI service uses this
              template as the fallback ack when no tag matches. */}
          <label className="flex items-start gap-2 p-3 rounded-md border border-[#E5E2D9] bg-[#FAF8F2] cursor-pointer">
            <input type="checkbox" checked={isAutoAck}
                   onChange={e => setIsAutoAck(e.target.checked)}
                   data-testid="rt-is-auto-ack"
                   className="mt-0.5"/>
            <span>
              <span className="text-sm font-medium block">Use as grievance auto-acknowledgement</span>
              <span className="text-[11px] text-[#5C5A55] block">
                When a new grievance comes in and no tag-matched template exists,
                Rigo AI will send this template as the first reply so the customer
                knows they&apos;ve been heard within seconds.
              </span>
            </span>
          </label>
          <button type="submit" disabled={saving}
                  data-testid="rt-save"
                  className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
            <FloppyDisk size={14} weight="fill"/>{saving ? "Saving…" : (isNew ? "Create template" : "Save changes")}
          </button>
        </form>
      </div>
    </div>
  );
}
